import { apiClient, ApiResponse } from './apiClient';

// Property interfaces matching your API structure
export interface Property {
  id: number;
  name: string;
  description: string;
  type_id: number;
  location: string;
  address: string;
  latitude?: string | number;
  longitude?: string | number;
  full_price: string | number;
  deposit_type: 'fixed' | 'percentage';
  deposit_value: string | number;
  calculated_deposit?: string | number;
  capacity: number;
  bedrooms: number;
  bathrooms: number;
  amenities: string | string[]; // API returns JSON string, we parse to array
  check_in_time: string;
  check_out_time: string;
  is_available?: boolean;
  is_active?: boolean;
  featured?: boolean;
  active?: boolean; // For compatibility
  rating?: string | number;
  reviews_count?: number;
  created_by?: string;
  createdAt: string;
  updatedAt: string;
  
  // Nested objects from API
  PropertyType?: {
    id: number;
    name_ar: string;
    name_en: string;
    icon: string;
  };
  Media?: PropertyImage[];
  Creator?: {
    id: string;
    name: string;
  };
  
  // Legacy fields for compatibility
  images?: PropertyImage[];
  properties_count?: number;
  average_rating?: number;
  total_bookings?: number;
}

export interface PropertyImage {
  id: number;
  property_id: number;
  url: string;
  type: 'image' | 'video';
  alt_text?: string;
  title?: string;
  description?: string;
  is_primary: boolean;
  created_at: string;
}

export interface PropertySearchFilters {
  // Required pagination
  page?: number;
  limit?: number;
  
  // Optional filters
  type_id?: number;
  location?: string;
  min_price?: number;
  max_price?: number;
  capacity?: number;
  featured?: boolean;
  available_only?: boolean;
  
  // Sorting
  sort_by?: 'createdAt' | 'updatedAt' | 'name' | 'full_price' | 'capacity';
  sort_order?: 'ASC' | 'DESC';
  
  // Search term (for client-side filtering if needed)
  searchTerm?: string;
}

export interface PropertyListResponse {
  properties: Property[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    per_page: number;
  };
}

export interface CreatePropertyRequest {
  name: string;
  description_en?: string;
  description_ar?: string;
  type_id: number;
  location: string;
  address: string;
  latitude?: number;
  longitude?: number;
  full_price: number;
  deposit_type: 'fixed' | 'percentage';
  deposit_value: number;
  capacity: number;
  bedrooms: number;
  bathrooms: number;
  amenities: string[];
  check_in_time: string;
  check_out_time: string;
  featured?: boolean;
  active?: boolean;
}

export interface UpdatePropertyRequest {
  name?: string;
  description?: string;
  type_id?: number;
  location?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  full_price?: number;
  deposit_type?: 'fixed' | 'percentage';
  deposit_value?: number;
  capacity?: number;
  bedrooms?: number;
  bathrooms?: number;
  amenities?: string[];
  check_in_time?: string;
  check_out_time?: string;
  featured?: boolean;
  active?: boolean;
}

// Properties Service Class
export class PropertiesService {
  private baseUrl = '/properties';

  // Get properties with search and filters (matches your GET /properties)
  async getProperties(filters: PropertySearchFilters = {}): Promise<ApiResponse<PropertyListResponse>> {
    const params = new URLSearchParams();
    
    // Required pagination parameters
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    // Optional filter parameters
    if (filters.type_id) params.append('type_id', filters.type_id.toString());
    if (filters.location) params.append('location', filters.location);
    if (filters.min_price) params.append('min_price', filters.min_price.toString());
    if (filters.max_price) params.append('max_price', filters.max_price.toString());
    if (filters.capacity) params.append('capacity', filters.capacity.toString());
    if (filters.featured !== undefined) params.append('featured', filters.featured.toString());
    if (filters.available_only !== undefined) params.append('available_only', filters.available_only.toString());
    
    // Sorting parameters
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    try {
      console.log('🔧 PropertiesService.getProperties called:', {
        url,
        filters
      });
      
      const response = await apiClient.get<PropertyListResponse>(url);
      
      console.log('🔧 PropertiesService.getProperties raw response:', response);
      
      if (response.success && response.data) {
        // Your API returns the data in the expected format already
        const propertiesData = response.data;
        
        console.log('🔧 PropertiesService.getProperties final result:', {
          propertiesCount: propertiesData.properties?.length || 0,
          pagination: propertiesData.pagination,
          propertiesData
        });
        
        return {
          success: true,
          data: propertiesData,
          message: 'Properties fetched successfully'
        };
      }
      
      return {
        success: false,
        data: { 
          properties: [], 
          pagination: { current_page: 1, total_pages: 0, total_items: 0, per_page: 10 }
        },
        message: 'Failed to fetch properties'
      };
    } catch (error) {
      console.error('🔧 PropertiesService.getProperties error:', error);
      return {
        success: false,
        data: { 
          properties: [], 
          pagination: { current_page: 1, total_pages: 0, total_items: 0, per_page: 10 }
        },
        message: 'Error fetching properties'
      };
    }
  }

  // Get property by ID
  async getPropertyById(id: number): Promise<ApiResponse<Property>> {
    try {
      console.log('🔧 PropertiesService.getPropertyById called:', { id });
      
      const response = await apiClient.get<Property>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 PropertiesService.getPropertyById response:', response);
      
      return response;
    } catch (error) {
      console.error('🔧 PropertiesService.getPropertyById error:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error fetching property'
      };
    }
  }

  // Create new property with form data (including media files)
  async createProperty(data: CreatePropertyRequest, mediaFiles?: File[]): Promise<ApiResponse<Property>> {
    try {
      console.log('🔧 PropertiesService.createProperty called:', { data, mediaFiles });
      console.log('🔧 Input data details:', {
        name: data.name,
        description_en: data.description_en,
        description_ar: data.description_ar,
        type_id: data.type_id,
        location: data.location,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        full_price: data.full_price,
        deposit_type: data.deposit_type,
        deposit_value: data.deposit_value,
        capacity: data.capacity,
        bedrooms: data.bedrooms,
        bathrooms: data.bathrooms,
        amenities: data.amenities,
        check_in_time: data.check_in_time,
        check_out_time: data.check_out_time,
        featured: data.featured,
        active: data.active
      });
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Add all property fields to FormData
      formData.append('name', data.name);
      formData.append('description_en', data.description_en || '');
      formData.append('description_ar', data.description_ar || '');
      formData.append('type_id', data.type_id.toString());
      formData.append('location', data.location);
      formData.append('address', data.address);
      
      if (data.latitude) formData.append('latitude', data.latitude.toString());
      if (data.longitude) formData.append('longitude', data.longitude.toString());
      
      formData.append('full_price', data.full_price.toString());
      formData.append('deposit_type', data.deposit_type);
      formData.append('deposit_value', data.deposit_value.toString());
      formData.append('capacity', data.capacity.toString());
      formData.append('bedrooms', data.bedrooms.toString());
      formData.append('bathrooms', data.bathrooms.toString());
      
      // Handle amenities array
      if (data.amenities && data.amenities.length > 0) {
        formData.append('amenities', JSON.stringify(data.amenities));
      }
      
      // Ensure time format includes seconds (HH:MM:SS)
      const formatTime = (time: string) => {
        if (time && time.length === 5) { // HH:MM format
          return time + ':00'; // Convert to HH:MM:SS
        }
        return time;
      };
      
      const formattedCheckIn = formatTime(data.check_in_time);
      const formattedCheckOut = formatTime(data.check_out_time);
      
      console.log('🔧 Time formatting:', {
        original_check_in: data.check_in_time,
        formatted_check_in: formattedCheckIn,
        original_check_out: data.check_out_time,
        formatted_check_out: formattedCheckOut
      });
      
      formData.append('check_in_time', formattedCheckIn);
      formData.append('check_out_time', formattedCheckOut);
      
      if (data.featured !== undefined) formData.append('featured', data.featured.toString());
      if (data.active !== undefined) formData.append('active', data.active.toString());
      
      // Add media files
      if (mediaFiles && mediaFiles.length > 0) {
        mediaFiles.forEach((file, index) => {
          formData.append('media', file);
        });
      }
      
      console.log('🔧 FormData being sent to API:');
      console.log('name:', formData.get('name'));
      console.log('description_en:', formData.get('description_en'));
      console.log('description_ar:', formData.get('description_ar'));
      console.log('type_id:', formData.get('type_id'));
      console.log('location:', formData.get('location'));
      console.log('address:', formData.get('address'));
      console.log('latitude:', formData.get('latitude'));
      console.log('longitude:', formData.get('longitude'));
      console.log('full_price:', formData.get('full_price'));
      console.log('deposit_type:', formData.get('deposit_type'));
      console.log('deposit_value:', formData.get('deposit_value'));
      console.log('capacity:', formData.get('capacity'));
      console.log('bedrooms:', formData.get('bedrooms'));
      console.log('bathrooms:', formData.get('bathrooms'));
      console.log('amenities:', formData.get('amenities'));
      console.log('check_in_time:', formData.get('check_in_time'));
      console.log('check_out_time:', formData.get('check_out_time'));
      console.log('featured:', formData.get('featured'));
      console.log('active:', formData.get('active'));
      console.log('media files count:', mediaFiles?.length || 0);
      
      // Send FormData instead of JSON
      // Note: Don't set Content-Type header manually for FormData - let browser set it with boundary
      const response = await apiClient.post<Property>(this.baseUrl, formData);
      
      console.log('🔧 PropertiesService.createProperty response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PropertiesService.createProperty error:', error);
      
      // Extract detailed error message from API response
      let errorMessage = 'Error creating property';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: undefined,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Update property
  async updateProperty(id: number, data: UpdatePropertyRequest): Promise<ApiResponse<Property>> {
    try {
      console.log('🔧 PropertiesService.updateProperty called:', { id, data });
      
      const response = await apiClient.patch<Property>(`${this.baseUrl}/${id}`, data);
      
      console.log('🔧 PropertiesService.updateProperty response:', response);
      
      return response;
    } catch (error) {
      console.error('🔧 PropertiesService.updateProperty error:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error updating property'
      };
    }
  }

  // Delete property
  async deleteProperty(id: number): Promise<ApiResponse<void>> {
    try {
      console.log('🔧 PropertiesService.deleteProperty called:', { id });
      
      const response = await apiClient.delete<void>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 PropertiesService.deleteProperty response:', response);
      
      return response;
    } catch (error) {
      console.error('🔧 PropertiesService.deleteProperty error:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error deleting property'
      };
    }
  }

  // Toggle property featured status
  async togglePropertyFeatured(id: number, featured: boolean): Promise<ApiResponse<Property>> {
    try {
      const response = await apiClient.patch<Property>(`${this.baseUrl}/${id}`, { featured });
      return response;
    } catch (error) {
      console.error('🔧 PropertiesService.togglePropertyFeatured error:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error updating property featured status'
      };
    }
  }

  // Toggle property active status
  async togglePropertyActive(id: number, active: boolean): Promise<ApiResponse<Property>> {
    try {
      const response = await apiClient.patch<Property>(`${this.baseUrl}/${id}`, { active });
      return response;
    } catch (error) {
      console.error('🔧 PropertiesService.togglePropertyActive error:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error updating property active status'
      };
    }
  }

  // Bulk operations
  async bulkUpdateProperties(ids: number[], action: string, data?: any): Promise<ApiResponse<void>> {
    // For now, use individual API calls for each property
    const promises = ids.map(id => {
      switch (action) {
        case 'activate':
          return this.togglePropertyActive(id, true);
        case 'deactivate':
          return this.togglePropertyActive(id, false);
        case 'feature':
          return this.togglePropertyFeatured(id, true);
        case 'unfeature':
          return this.togglePropertyFeatured(id, false);
        case 'delete':
          return this.deleteProperty(id);
        default:
          return Promise.resolve({ success: true, data: null });
      }
    });

    await Promise.all(promises);
    return { success: true, data: undefined, message: 'Bulk operation completed' };
  }

  // Get property statistics (if available from API)
  async getPropertyStats(): Promise<ApiResponse<any>> {
    try {
      // This endpoint might not exist yet, so we'll calculate from the properties list
      const response = await this.getProperties({ page: 1, limit: 1000 });
      if (response.success && response.data) {
        const properties = response.data.properties;
        const stats = {
          total: response.data.pagination.total_items,
          active: properties.filter(p => p.active).length,
          inactive: properties.filter(p => !p.active).length,
          featured: properties.filter(p => p.featured).length,
          average_price: properties.length > 0 ? 
            properties.reduce((sum, p) => sum + p.full_price, 0) / properties.length : 0,
        };
        return { success: true, data: stats, message: 'Stats calculated' };
      }
      throw new Error('Failed to fetch properties for stats');
    } catch (error) {
      // Return default stats if calculation fails
      return { 
        success: true, 
        data: { total: 0, active: 0, inactive: 0, featured: 0, average_price: 0 }, 
        message: 'Default stats' 
      };
    }
  }
}

// Export singleton instance
export const propertiesService = new PropertiesService();
export default propertiesService;
